import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  Button,
  FormLabel,
  Typography,
  FormHelperText,
  Container,
  Paper,
} from "@material-ui/core";
import { NavLink as RouterLink } from "react-router-dom";
import Skeleton from "@material-ui/lab/Skeleton";
import UserAndDate from "src/components/common/UserAndDate";
import parse from "html-react-parser";
import { useForm, Controller } from "react-hook-form";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { convertToRaw } from "draft-js";
import MUIRichTextEditor from "mui-rte";
import { kmMessages } from "src/constant/Messages";
import SaveIcon from "@material-ui/icons/Save";
import SendIcon from "@material-ui/icons/Send";

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
  validate: `${kmMessages.error.form_validation.field_wronginputformat}`,
};

const mailingStatuses = {
  draft: "Wersja robocza",
  pending: "Oczekiwanie na wysyłkę",
  sending: "Wysyłanie",
  sent: "Wysłane",
  partially_sent: "Częściowo wysłane",
  error: "Błąd",
};

const footerLabels = {
  nofooter: "brak",
  notification_messages: "stopka powiadomień",
  system_messages: "stopka wiadomości systemowych",
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(2),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  paddingpaper: {
    padding: theme.spacing(1),
    "& > ol, & > ul": {
      marginLeft: theme.spacing(4),
    },
  },
  paddingtopbottom: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
  margintop: {
    marginTop: theme.spacing(1),
  },
}));

const Form = ({ systemInfo, onSubmit, mailing, ...rest }) => {
  const classes = useStyles();

  const [nextSubmitTry, setNextSubmitTry] = useState(false);
  const [contentValue, setContentValue] = useState(null);
  const [htmlContentValue, setHtmlContentValue] = useState("");
  const [contentEmpty, setContentEmpty] = useState(true);

  const [mailingStatus, setMailingStatus] = useState();

  const { handleSubmit, setValue, control, errors } = useForm();

  useEffect(() => {
    if (mailing) {
      setContentRteState(mailing.bodyRaw);
    }

    return () => {};
  }, [mailing]);

  const handleContentChange = (event) => {
    let htmlState = stateToHTML(event.getCurrentContent());

    setContentEmpty(!event.getCurrentContent().hasText());

    setHtmlContentValue(htmlState);
  };

  const setContentRteState = (contentHtml) => {
    let contentState = stateFromHTML(contentHtml);
    setContentValue(JSON.stringify(convertToRaw(contentState)));
  };

  const onFormSubmit = (values) => {
    if (!contentEmpty) {
      setContentEmpty(false);
      onSubmit(values, mailing.id, htmlContentValue, mailingStatus);
    } else {
      setContentEmpty(true);
      setNextSubmitTry(true);
    }
  };

  return (
    <Card className={clsx(classes.root)}>
      <Box component="main">
        <Grid container className={classes.root}>
          <Grid item xs={12} elevation={2}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              {mailing ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <FormLabel
                    className={classes.paddingBottom}
                    component="label"
                  >
                    Rodzaj wiadomości
                  </FormLabel>
                  <Typography>{mailing.messageTypeName}</Typography>
                </FormControl>
              ) : (
                <Skeleton />
              )}

              {mailing ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <FormLabel
                    className={classes.paddingBottom}
                    component="label"
                  >
                    Temat
                  </FormLabel>
                  <Typography>{mailing.subject}</Typography>
                </FormControl>
              ) : (
                <Skeleton />
              )}

              {mailing ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} sm={4}>
                      <FormControl fullWidth className={classes.formcontrol}>
                        <FormLabel
                          className={classes.paddingBottom}
                          component="label"
                        >
                          Status
                        </FormLabel>
                        <Typography>
                          {mailingStatuses[mailing.status]}
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} sm={4}>
                      <FormLabel
                        className={classes.paddingBottom}
                        component="label"
                      >
                        Kto i kiedy utworzył
                      </FormLabel>
                      <UserAndDate
                        variant="body1"
                        username={mailing.createdBy}
                        date={mailing.createdAt}
                        separator=", "
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} sm={4}>
                      <FormLabel
                        className={classes.paddingBottom}
                        component="label"
                      >
                        Kto i kiedy modyfikował
                      </FormLabel>
                      <UserAndDate
                        variant="body1"
                        username={mailing.updatedBy}
                        date={mailing.updatedAt}
                        separator=", "
                      />
                    </Grid>
                  </Grid>
                </FormControl>
              ) : (
                <Skeleton />
              )}

              {mailing ? (
                <FormControl
                  fullWidth
                  className={classes.formcontrol}
                  variant="outlined"
                >
                  <Box className={classes.paddingbottom}>
                    <FormLabel for="newscontentrte">
                      Treść wiadomości:
                    </FormLabel>
                  </Box>
                  <Paper variant="outlined" className={classes.rtepaper}>
                    <MUIRichTextEditor
                      label="Wprowadź treść wiadomości"
                      defaultValue={contentValue}
                      className={classes.rtecontroll}
                      id="newscontentrte"
                      controls={[
                        "title",
                        "bold",
                        "italic",
                        "underline",
                        "strikethrough",
                        "highlight",
                        "undo",
                        "redo",
                        "link",
                        "media",
                        "numberList",
                        "bulletList",
                        "quote",
                        "clear",
                      ]}
                      onChange={handleContentChange}
                      inlineToolbar={true}
                    />
                  </Paper>
                  {nextSubmitTry && contentEmpty ? (
                    <FormHelperText id="mailing-content-helper" error>
                      {formValidation.required}
                    </FormHelperText>
                  ) : (
                    <></>
                  )}
                </FormControl>
              ) : (
                <Skeleton />
              )}

              {mailing ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <FormLabel
                    className={classes.paddingBottom}
                    component="label"
                  >
                    Stopka
                  </FormLabel>
                  <Typography>
                    {mailing.footerVersion
                      ? footerLabels[mailing.footerVersion]
                      : "-"}
                  </Typography>
                </FormControl>
              ) : (
                <Skeleton />
              )}

              <Box className={classes.paddingtop}>
                <Button
                  color="primary"
                  className={classes.button}
                  component={RouterLink}
                  to={`/powiadomienia`}
                >
                  Anuluj
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SaveIcon />}
                  onClick={() => setMailingStatus("draft")}
                  type="submit"
                >
                  Zapisz
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SendIcon />}
                  onClick={() => setMailingStatus("pending")}
                  type="submit"
                >
                  Zapisz i wyślij
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Form;
